import { apiClient } from './api';

const API_BASE_URL = import.meta.env.VITE_API_URL;

export interface VerifyLogsResponse {
  success: boolean;
  token?: string;
  expires_at?: string;
  message: string;
}

export interface LogFileResponse {
  lines: string[];
  total_lines: number;
  file_size: number;
}

export interface LogFileInfo {
  index: number;
  name: string;
  path: string;
  is_current: boolean;
  size_bytes: number;
  size_mb: number;
  modified: string;
  first_timestamp: string | null;
  last_timestamp: string | null;
}

export interface LogFilesListResponse {
  files: LogFileInfo[];
  total: number;
}

export interface SessionInfo {
  session_id: string;
  started_at: string | null;
  file_path: string;
  is_current?: boolean;
}

export interface SessionsListResponse {
  sessions: SessionInfo[];
  total: number;
}

export interface SessionLogsResponse {
  session_id: string;
  lines: string[];
  total_lines: number;
}

/**
 * Verify admin password and get logs access token
 */
export const verifyLogsPassword = async (password: string): Promise<VerifyLogsResponse> => {
  const response = await apiClient.post<VerifyLogsResponse>('/api/logs/verify', { password });
  return response.data;
};

/**
 * List available log files with metadata
 */
export const listLogFiles = async (token: string): Promise<LogFilesListResponse> => {
  const response = await apiClient.get<LogFilesListResponse>('/api/logs/files', {
    params: { token }
  });
  return response.data;
};

/**
 * Get log file content (paginated)
 */
export const getLogFile = async (
  token: string,
  fileIndex = 0,
  lines = 10000,
  offset = 0
): Promise<LogFileResponse> => {
  const response = await apiClient.get<LogFileResponse>('/api/logs/file', {
    params: { token, file_index: fileIndex, lines, offset }
  });
  return response.data;
};

/**
 * List chat sessions from logs
 */
export const listSessions = async (token: string): Promise<SessionsListResponse> => {
  const response = await apiClient.get<SessionsListResponse>('/api/logs/sessions', {
    params: { token }
  });
  return response.data;
};

/**
 * Get logs for a specific session
 */
export const getSessionLogs = async (
  token: string,
  sessionId: string,
  maxLines = 5000
): Promise<SessionLogsResponse> => {
  const response = await apiClient.get<SessionLogsResponse>(`/api/logs/session/${sessionId}`, {
    params: { token, max_lines: maxLines }
  });
  return response.data;
};

/**
 * Get WebSocket URL for log streaming
 */
export const getLogsWebSocketUrl = (token: string): string => {
  if (!API_BASE_URL) {
    throw new Error('API_BASE_URL not configured');
  }

  // Convert HTTP URL to WebSocket URL
  const wsUrl = API_BASE_URL.replace(/^http/, 'ws');
  return `${wsUrl}/api/logs/stream?token=${encodeURIComponent(token)}`;
};

/**
 * WebSocket message types
 */
export interface LogsWebSocketMessage {
  type: 'connected' | 'log' | 'history_end' | 'error' | 'pong';
  message?: string;
  line?: string;
  file?: string;
}
